import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarDays, Globe } from "lucide-react";
import { COMPANY } from "../content";
import { useLanguage } from "../i18n";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-[#141d43] shadow-lg shadow-black/20" : "bg-[#141d43]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <span className="bg-white rounded-md px-2.5 py-1.5 flex items-center">
              <img src={COMPANY.logo} alt="INEXPONENS" className="h-9 w-auto object-contain" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-white font-semibold tracking-[0.14em] text-sm whitespace-nowrap">INEXPONENS</span>
              <span className="text-white/60 text-[10px] tracking-[0.22em] uppercase whitespace-nowrap">Audit &amp; Advisory</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-stretch h-full" data-testid="desktop-nav">
            {t.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center whitespace-nowrap px-3.5 xl:px-4 h-20 text-[13px] xl:text-[13.5px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive
                      ? "bg-white text-[#1b2653]"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 border border-white/30 text-white text-[12px] font-semibold uppercase tracking-wider hover:bg-white/10 transition-colors duration-300"
              data-testid="lang-toggle"
              aria-label="Changer de langue / Switch language"
            >
              <Globe size={14} className="text-[#6ea8ff]" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <Link
              to="/rendez-vous"
              onClick={() => window.scrollTo({ top: 0 })}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 bg-white text-[#1b2653] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#2f6fd8] hover:text-white transition-colors duration-300"
              data-testid="header-rdv-btn"
            >
              <CalendarDays size={15} />
              {t.rdvBtn}
            </Link>
          </div>

          {/* Mobile: lang + toggle */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleLang}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-white/30 text-white text-[11px] font-semibold uppercase tracking-wider"
              data-testid="lang-toggle-mobile"
            >
              <Globe size={13} className="text-[#6ea8ff]" />
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <button
              className="text-white p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[520px]" : "max-h-0"
        } bg-[#141d43]`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col px-5 pb-6 pt-2">
          {t.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `py-3.5 px-3 rounded-md text-[15px] border-b border-white/10 transition-colors ${
                  isActive ? "text-white font-semibold bg-white/10" : "text-white/80"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-white/90 pt-4 px-3 text-sm">
            <Phone size={16} className="text-[#6ea8ff]" /> {COMPANY.phone}
          </a>
          <Link
            to="/rendez-vous"
            className="mt-4 mx-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-white text-[#1b2653] text-[12px] font-semibold uppercase tracking-[0.14em]"
            data-testid="mobile-rdv-btn"
          >
            <CalendarDays size={15} />
            {t.rdvBtnMobile}
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
